import _ from 'lodash';
import { getPromptsKeysFromServerless } from './fetchDataUtils';
import { ImageDescription } from './types';
import { createPromiseWithResolvers } from '../utils/promiseWithResolvers';
import { FedopsInteraction } from '../types';

const DEBOUNCE_TIME: number = 100;

export interface PromptGenerationParams {
  sectionName: string;
  structurePromptVer: string;
  scenePromptVer: string;
  siteDescription?: string;
  businessTerm: string;
  msId: string;
  msInstance: string;
}

export type PromptsMap = Record<string, string>;

export class DebouncedPromptGenerator {
  private imageDescriptions: Record<string, ImageDescription>;
  private timer: ReturnType<typeof setTimeout>;
  private readonly uniqId: string = (Math.random() * 1000).toFixed(0);

  private readonly promiseWithResolvers =
    createPromiseWithResolvers<PromptsMap>();

  constructor(
    private readonly params: PromptGenerationParams,
    private readonly onGenerationStart: VoidFunction,
    private readonly interactionStarted: FedopsInteraction,
    private readonly interactionEnded: FedopsInteraction,
  ) {
    this.interactionStarted('sg_generate_image_prompts', {
      customParams: { uniqId: this.uniqId },
    });
    this.resceduleExecution();
  }

  private resceduleExecution() {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(this.executeGeneration.bind(this), DEBOUNCE_TIME);
  }

  private async executeGeneration() {
    this.onGenerationStart();
    const { resolve, reject } = this.promiseWithResolvers;
    const { msId, msInstance, sectionName, ...promptParams } = this.params;
    let data: Awaited<ReturnType<typeof getPromptsKeysFromServerless>>;
    try {
      data = await getPromptsKeysFromServerless(
        {
          ...promptParams,
          imageDescriptions: this.imageDescriptions,
        },
        msId,
        msInstance,
      );
      if (!data?.results) {
        reject(
          new Error(`Failed to generate prompts for section: ${sectionName}`),
        );
        return;
      }
      if (_.isEmpty(data.results)) {
        reject(
          new Error(`Server response is empty for section: ${sectionName}`),
        );
        return;
      }
      resolve(data.results);
    } catch (err) {
      reject(
        new Error(
          `Failed to generate prompts for section: ${sectionName}. Error: ${err}`,
        ),
      );
    } finally {
      this.interactionEnded('sg_generate_image_prompts', {
        customParams: { uniqId: this.uniqId },
      });
    }
  }

  public async generate(
    imageDescriptions: Record<string, ImageDescription>,
  ): Promise<PromptsMap> {
    this.imageDescriptions = {
      ...this.imageDescriptions,
      ...imageDescriptions,
    };
    this.resceduleExecution();
    return this.promiseWithResolvers.promise;
  }
}

export class PromptGeneratorQueuer {
  private queue: Map<string, DebouncedPromptGenerator> = new Map();

  public async generate(
    params: PromptGenerationParams,
    imagesDescription: Record<string, object>,
    interactionStarted: FedopsInteraction = _.noop,
    interactionEnded: FedopsInteraction = _.noop,
  ): Promise<Record<string, string>> {
    const hash = `${params.structurePromptVer}___${params.scenePromptVer}`;
    if (!this.queue.has(hash)) {
      const finalCallback = () => this.queue.delete(hash);
      const generator = new DebouncedPromptGenerator(
        params,
        finalCallback,
        interactionStarted,
        interactionEnded,
      );
      this.queue.set(hash, generator);
    }

    const generator = this.queue.get(hash);
    const results = await generator!.generate(imagesDescription);
    return _.pick(results, ...Object.keys(imagesDescription));
  }
}
